import React, { useState, useEffect } from 'react';
import * as OTPAuth from 'otpauth';

const TotpDisplay = ({ totpSecret, username }) => {
    const [code, setCode] = useState('------');
    const [timeLeft, setTimeLeft] = useState(30);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!totpSecret) return;

        const generateCode = () => {
            try {
                const totp = new OTPAuth.TOTP({
                    issuer: 'Calendar',
                    label: username,
                    algorithm: 'SHA1',
                    digits: 6,
                    period: 30,
                    secret: OTPAuth.Secret.fromBase32(totpSecret)
                });

                const token = totp.generate();
                setCode(token);

                // Calculate time left in current period
                const now = Math.floor(Date.now() / 1000);
                const remaining = 30 - (now % 30);
                setTimeLeft(remaining);
            } catch (error) {
                console.error('Error generating TOTP:', error);
                setCode('ERROR');
            }
        };

        // Generate immediately
        generateCode();

        // Update every second
        const interval = setInterval(generateCode, 1000);

        return () => clearInterval(interval);
    }, [totpSecret, username]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    if (!totpSecret) {
        return null;
    }

    return (
        <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                Authenticator Code
            </div>
            <div
                onClick={handleCopy}
                className="flex items-center justify-between cursor-pointer bg-white hover:bg-gray-50 p-3 rounded-lg shadow-sm transition-all border border-gray-200"
                title="Click to copy"
            >
                <div className="flex items-center space-x-3">
                    <span className="text-2xl font-mono font-bold tracking-widest text-blue-600">
                        {code.substring(0, 3)} {code.substring(3, 6)}
                    </span>
                    {copied && (
                        <span className="text-xs text-green-600 font-semibold animate-pulse">
                            ✓ Copied!
                        </span>
                    )}
                </div>
                <div className="flex items-center space-x-3">
                    <div className="relative w-10 h-10">
                        <svg className="transform -rotate-90 w-10 h-10">
                            <circle
                                cx="20"
                                cy="20"
                                r="16"
                                stroke="currentColor"
                                strokeWidth="3"
                                fill="none"
                                className="text-gray-200"
                            />
                            <circle
                                cx="20"
                                cy="20"
                                r="16"
                                stroke="currentColor"
                                strokeWidth="3"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - timeLeft / 30)}`}
                                className={`${timeLeft <= 5 ? 'text-red-500' : 'text-blue-500'} transition-all duration-300`}
                                strokeLinecap="round"
                            />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700">
                            {timeLeft}
                        </span>
                    </div>
                    <i className="bi bi-clipboard text-lg text-gray-500"></i>
                </div>
            </div>
        </div>
    );
};

export default TotpDisplay;
